import { useIntl } from 'react-intl';

import { toNumber } from './likertHelpers';
import {
  getFaixaFavorabilidade,
  getFaixasFavorabilidade,
  getLimitesFavorabilidade,
} from './favorabilidadeHelpers';

const styles = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #eef0f3',
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    marginBottom: 24,
    padding: '20px 22px',
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  title: {
    color: '#101828',
    fontSize: 15,
    fontWeight: 700,
  },
  subtitle: {
    color: '#98a2b3',
    fontSize: 13,
    marginTop: 4,
  },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
  },
  body: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 28,
  },
  scoreColumn: {
    flex: '0 0 auto',
    minWidth: 150,
  },
  score: {
    fontSize: 56,
    fontWeight: 800,
    lineHeight: 1,
  },
  scoreHint: {
    color: '#98a2b3',
    fontSize: 13,
    marginTop: 8,
  },
  gaugeColumn: {
    display: 'flex',
    flex: '1 1 340px',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
  },
  gaugeTrack: {
    borderRadius: 999,
    height: 10,
    overflow: 'hidden',
    position: 'relative',
  },
  gaugeTick: {
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    bottom: 0,
    position: 'absolute',
    top: 0,
    transform: 'translateX(-50%)',
    width: 2,
  },
  gaugeMarkerArea: {
    height: 10,
    marginTop: -10,
    position: 'relative',
  },
  gaugeMarker: {
    background: '#101828',
    border: '2px solid #fff',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.35)',
    height: 18,
    position: 'absolute',
    top: -4,
    transform: 'translateX(-50%)',
    width: 18,
  },
  gaugeScale: {
    color: '#98a2b3',
    fontSize: 11,
    height: 16,
    marginTop: 8,
    position: 'relative',
  },
  gaugeScaleItem: {
    position: 'absolute',
    top: 0,
    whiteSpace: 'nowrap',
  },
  base: {
    color: '#667085',
    fontSize: 13,
    marginTop: 14,
  },
  empty: {
    color: '#98a2b3',
    fontSize: 14,
  },
};

const clampPercent = (value) => Math.min(Math.max(toNumber(value), 0), 100);

function FavorabilidadeCard({ dados }) {
  const intl = useIntl();

  if (!dados) return null;

  const faixas = getFaixasFavorabilidade(dados.faixas);
  const limites = getLimitesFavorabilidade(dados.faixas);
  const limiteInicial = limites[0];
  const limiteFinal = limites[limites.length - 1];
  const amplitude = limiteFinal - limiteInicial || 100;

  const posicao = (valor) => ((valor - limiteInicial) / amplitude) * 100;

  const totalRespostasValidas = toNumber(dados.totalRespostasValidas);
  const totalFavoraveis = toNumber(dados.favoraveis?.quantidade);
  const quantidadePerguntas = toNumber(dados.quantidadePerguntasLikert);
  const possuiResultado =
    totalRespostasValidas > 0 && dados.favorabilidadePercentual != null;

  const valor = clampPercent(dados.favorabilidadePercentual);
  const faixa = getFaixaFavorabilidade(valor, dados.faixas);

  const formatPercent = (value, casas = 1) =>
    `${intl.formatNumber(toNumber(value), {
      maximumFractionDigits: casas,
      minimumFractionDigits: casas,
    })}%`;

  const gradiente = `linear-gradient(90deg, ${faixas
    .map(
      (item) =>
        `${item.color} ${posicao(item.limiteInferior)}%, ${
          item.color
        } ${posicao(item.limiteSuperior)}%`
    )
    .join(', ')})`;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>
            {intl.formatMessage({ id: 'labelFavorabilidade' })}
          </div>
          <div style={styles.subtitle}>
            {intl.formatMessage({ id: 'labelFavorabilidadeDescricao' })}
          </div>
        </div>
        {possuiResultado && (
          <span style={{ ...styles.badge, backgroundColor: faixa.color }}>
            {intl.formatMessage({ id: faixa.labelId })}
          </span>
        )}
      </div>

      <div style={styles.body}>
        <div style={styles.scoreColumn}>
          <div
            style={{
              ...styles.score,
              color: possuiResultado ? faixa.color : '#98a2b3',
            }}
          >
            {possuiResultado ? formatPercent(valor) : '–'}
          </div>
          <div style={styles.scoreHint}>
            {intl.formatMessage(
              {
                id:
                  quantidadePerguntas === 1
                    ? 'labelFavorabilidadePerguntaConsiderada'
                    : 'labelFavorabilidadePerguntasConsideradas',
              },
              { quantidade: quantidadePerguntas }
            )}
          </div>
        </div>

        <div style={styles.gaugeColumn}>
          <div style={{ ...styles.gaugeTrack, background: gradiente }}>
            {limites.slice(1, -1).map((limite) => (
              <span
                key={limite}
                style={{ ...styles.gaugeTick, left: `${posicao(limite)}%` }}
              />
            ))}
          </div>
          <div style={styles.gaugeMarkerArea}>
            {possuiResultado && (
              <div
                style={{ ...styles.gaugeMarker, left: `${posicao(valor)}%` }}
              />
            )}
          </div>
          <div style={styles.gaugeScale}>
            {limites.map((limite, index) => {
              const isFirst = index === 0;
              const isLast = index === limites.length - 1;

              return (
                <span
                  key={limite}
                  style={{
                    ...styles.gaugeScaleItem,
                    left: `${posicao(limite)}%`,
                    transform: isFirst
                      ? 'none'
                      : `translateX(${isLast ? '-100%' : '-50%'})`,
                  }}
                >
                  {formatPercent(limite, 0)}
                </span>
              );
            })}
          </div>
          <div style={possuiResultado ? styles.base : styles.empty}>
            {possuiResultado
              ? intl.formatMessage(
                  { id: 'labelFavorabilidadeBase' },
                  {
                    favoraveis: intl.formatNumber(totalFavoraveis),
                    total: intl.formatNumber(totalRespostasValidas),
                  }
                )
              : intl.formatMessage({ id: 'labelFavorabilidadeSemDados' })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FavorabilidadeCard;
